import React, { useMemo, useRef, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { MdApps, MdEditNote, MdFileUpload, MdInfoOutline, MdPalette, MdSave, MdVisibility } from "react-icons/md";
import { LuPlay, LuSquare } from "react-icons/lu";
import { useAuth } from "../../../Context/AuthContext";
import { APPRoutes } from "../../../Utils/Route";
import { isSuperAdminUser } from "../../../Utils/Permissions";
import {
    getAegisAiAssistConfigById,
    getAegisAiAssistIconUrl,
    saveAegisAiAssistConfig,
    uploadAegisAiAssistIcon,
} from "../../../Services/AegisAiAssistService";
import { getAllClientNames } from "../../../Services/ClientService";
import { getAllAegisAiDocuments } from "../../../Services/AegisAiDocumentsService";
import SearchableDropdown from "../../../Components/Subscription/SearchableDropdown";
import DotLottie from "../../../Components/Common/DotLottie";
import {
    ALL_ASSIST_ICONS,
    ASSIST_POSITION_OPTIONS,
    DEFAULT_ASSIST_ICONS,
    getAssistIconComponent,
    getAssistIconLabel,
} from "../../../Utils/AssistIcons";
import { AiFormSection } from "../AegisAiUi";
import AssistIconPickerModal from "./AssistIconPickerModal";
import AssistTrialWidget from "./AssistTrialWidget";
import { AssistEmbedModal } from "./AssistEmbedCode";


const DEFAULT_COLOR = "#1c4fa6";
const LOGO_TYPES = ["image/png", "image/jpeg", "image/webp"];
const MAX_LOGO_BYTES = 1024 * 1024;
const INLINE_ICON_COUNT = 3;

// A document is searchable once vector indexing has moved it from Pending to Active.
const isDocumentIndexed = (doc) => (doc?.Status || doc?.IndexingStatus || "").trim().toLowerCase() === "active";

const emptyForm = {
    id: null,
    clientId: null,
    title: "",
    description: "",
    primaryColor: DEFAULT_COLOR,
    icon: DEFAULT_ASSIST_ICONS[0].value,
    // Uploaded logo shown inside the chat window (required); the preset icon is the floating launcher.
    iconFileId: null,
    hasLogo: false,
    position: "bottom-right",
    fallbackMessage: "",
};

const validationSchema = Yup.object({
    title: Yup.string().trim().required("Title is required"),
    clientId: Yup.string().nullable().required("Client is required"),
    primaryColor: Yup.string().trim().required("Primary color is required"),
    hasLogo: Yup.boolean().oneOf([true], "Logo is required"),
    fallbackMessage: Yup.string().max(1000, "Fallback message must be 1000 characters or less"),
});

export default function AegisAiAssistForm() {
    const navigate = useNavigate();
    const { id } = useParams();
    const isEdit = !!id;

    const { token, decodedToken } = useAuth();
    const isSuperAdmin = isSuperAdminUser(decodedToken);
    const accountId = decodedToken?.account_id;

    const [loading, setLoading] = useState(isEdit);
    const [clients, setClients] = useState([]);
    const [clientsLoading, setClientsLoading] = useState(false);
    const [isIconModalOpen, setIsIconModalOpen] = useState(false);
    // Clients whose knowledge base has finished indexing; only these can get a widget.
    const [readyClientIds, setReadyClientIds] = useState(() => new Set());
    const [kbLoading, setKbLoading] = useState(true);
    const [trialState, setTrialState] = useState("off"); // off | on | exiting
    // Non-super-admins build widgets for their own organization only; its name comes from their documents.
    const [ownClientName, setOwnClientName] = useState("");
    // After a successful save the embed snippet is shown before returning to the list.
    const [savedWidget, setSavedWidget] = useState(null);
    // A newly picked logo file; it is uploaded once the widget is saved and has an id.
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreviewUrl, setLogoPreviewUrl] = useState(null);
    const logoInputRef = useRef(null);

    React.useEffect(() => {
        if (!logoFile) {
            setLogoPreviewUrl(null);
            return undefined;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreviewUrl(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    function mapApiToForm(configData) {
        return {
            id: configData?.Id ?? null,
            clientId: isSuperAdmin ? configData?.ClientId : accountId,
            title: configData?.Title ?? "",
            description: configData?.Description ?? "",
            primaryColor: configData?.PrimaryColor ?? DEFAULT_COLOR,
            icon: configData?.Icon ?? DEFAULT_ASSIST_ICONS[0].value,
            iconFileId: configData?.IconFileId ?? null,
            hasLogo: !!configData?.IconFileId,
            position: configData?.Position || configData?.position || "bottom-right",
            fallbackMessage: configData?.FallbackMessage ?? "",
        };
    }

    const formik = useFormik({
        initialValues: useMemo(() => ({ ...emptyForm, clientId: isSuperAdmin ? null : accountId }), [isSuperAdmin, accountId]),
        enableReinitialize: false,
        validationSchema,
        onSubmit: async (values) => {
            if (!values.id && !readyClientIds.has(String(isSuperAdmin ? values.clientId : accountId))) {
                toast.error("Widgets can only be created once the client's knowledge base has finished indexing.");
                return;
            }
            try {
                const payload = {
                    Id: values.id || null,
                    ClientId: isSuperAdmin ? values.clientId : accountId,
                    Title: values.title,
                    Description: values.description || null,
                    PrimaryColor: values.primaryColor,
                    Icon: values.icon,
                    Position: values.position || "bottom-right",
                    // Sent as "" when cleared so the saved message is removed (null would be ignored on update).
                    FallbackMessage: values.fallbackMessage.trim(),
                    Type: values.id ? "update" : "create",
                };
                const response = await saveAegisAiAssistConfig(payload, token);

                if (response?.is_success) {
                    toast.success(response?.message || "Widget saved successfully");
                    // The embed script is shown once, when a new agent gets its id; edits go straight back to the list.
                    const savedId = response?.data?.Id;
                    await saveLogo(values.id || savedId);
                    if (!values.id && savedId) {
                        setTrialState((state) => (state === "on" ? "exiting" : state));
                        setSavedWidget({ id: savedId, title: values.title });
                    } else {
                        navigate(APPRoutes.AEGIS_AI_ASSIST);
                    }
                } else if (response?.is_success === false && response?.status_code === 500) {
                    toast.error("Internal Server Error. Please Contact Support Team.");
                } else {
                    toast.error(response?.message || "Failed to save widget");
                }
            } catch (error) {
                console.error(error);
                toast.error(error.message || "Failed to save widget");
            }
        },
    });

    // The logo is stored per agent id, so it is saved after the widget itself.
    const saveLogo = async (agentId) => {
        if (!agentId || !logoFile) return;
        try {
            const response = await uploadAegisAiAssistIcon(agentId, logoFile, token);
            if (!response?.is_success) toast.error(response?.message || "Widget saved, but the logo could not be updated");
        } catch (error) {
            console.error(error);
            toast.error("Widget saved, but the logo could not be updated");
        }
    };

    const handleLogoSelect = (event) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        if (!LOGO_TYPES.includes(file.type)) {
            toast.error("Logo must be a PNG, JPEG or WebP image");
            return;
        }
        if (file.size > MAX_LOGO_BYTES) {
            toast.error("Logo must be 1 MB or smaller");
            return;
        }
        setLogoFile(file);
        formik.setFieldValue("hasLogo", true);
    };

    const selectPresetIcon = (value) => formik.setFieldValue("icon", value);

    const loadConfig = async () => {
        if (!isEdit) return;
        try {
            setLoading(true);
            const response = await getAegisAiAssistConfigById(id, token);
            if (response?.is_success) {
                formik.setValues(mapApiToForm(response?.data));
            } else if (response?.is_success === false && response?.status_code === 500) {
                toast.error("Internal Server Error. Please Contact Support Team.");
            } else {
                toast.error(response?.message || "Failed to load widget");
            }
        } catch (error) {
            console.error(error);
            toast.error(error.message || "Failed to load widget");
        } finally {
            setLoading(false);
        }
    };

    const loadClientsAndKnowledgeBases = async () => {
        try {
            setClientsLoading(isSuperAdmin);
            setKbLoading(true);
            const [clientsResponse, documentsResponse] = await Promise.all([
                isSuperAdmin ? getAllClientNames(token) : Promise.resolve(null),
                getAllAegisAiDocuments(token),
            ]);
            const allClients = isSuperAdmin && clientsResponse?.is_success && Array.isArray(clientsResponse?.data)
                ? clientsResponse.data
                : [];
            const documents = documentsResponse?.is_success && Array.isArray(documentsResponse?.data?.Documents)
                ? documentsResponse.data.Documents
                : [];
            const indexedDocuments = documents.filter(isDocumentIndexed);

            const ready = new Set();
            if (isSuperAdmin) {
                const readyIds = new Set(indexedDocuments.map((doc) => doc?.ClientId).filter(Boolean).map(String));
                const readyNames = new Set(indexedDocuments.map((doc) => (doc?.ClientName || "").trim().toLowerCase()).filter(Boolean));
                allClients.forEach((client) => {
                    if (readyIds.has(String(client.Id)) || readyNames.has((client.OrganizationName || "").trim().toLowerCase())) {
                        ready.add(String(client.Id));
                    }
                });
            } else {
                if (indexedDocuments.length > 0 && accountId) ready.add(String(accountId));
                setOwnClientName(
                    documents.find((doc) => doc?.ClientName)?.ClientName
                    || decodedToken?.organization_name
                    || decodedToken?.OrganizationName
                    || ""
                );
            }

            setClients(allClients);
            setReadyClientIds(ready);
        } catch (error) {
            console.error(error);
            setClients([]);
            setReadyClientIds(new Set());
        } finally {
            setClientsLoading(false);
            setKbLoading(false);
        }
    };

    React.useEffect(() => {
        if (!token) return;
        loadClientsAndKnowledgeBases();
        loadConfig();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isEdit, token, isSuperAdmin]);

    // Switching to a client without a ready knowledge base ends a running trial.
    const selectedClientId = formik.values.clientId;
    const selectedClientReady = !!selectedClientId && readyClientIds.has(String(selectedClientId));
    React.useEffect(() => {
        if (!selectedClientReady) setTrialState((state) => (state === "on" ? "exiting" : state));
    }, [selectedClientReady]);

    const handleTrialExited = React.useCallback(() => setTrialState("off"), []);
    const toggleTrial = () => setTrialState((state) => (state === "on" ? "exiting" : "on"));

    const clientOptions = clients
        .filter((client) => readyClientIds.has(String(client.Id)) || (isEdit && String(client.Id) === String(selectedClientId)))
        .map((client) => ({ value: client.Id, label: client.OrganizationName }));
    const selectedClientName = isSuperAdmin
        ? clients.find((client) => String(client.Id) === String(selectedClientId))?.OrganizationName
        : ownClientName;

    const renderAsterisk = () => <span className="text-danger">*</span>;
    const logoUrl = logoPreviewUrl || getAegisAiAssistIconUrl(formik.values.id, formik.values.iconFileId);
    const LauncherIcon = getAssistIconComponent(formik.values.icon);
    const ChatLogo = getAssistIconComponent(formik.values.icon, logoUrl);
    const logoError = formik.touched.hasLogo && formik.errors.hasLogo;

    // Only a few icons are shown inline (the rest are under "More"); a picked icon outside them takes the last slot.
    const displayIcons = useMemo(() => {
        const inline = DEFAULT_ASSIST_ICONS.slice(0, INLINE_ICON_COUNT);
        if (inline.some((opt) => opt.value === formik.values.icon)) return inline;
        const selected = ALL_ASSIST_ICONS.find((opt) => opt.value === formik.values.icon);
        return selected ? [...inline.slice(0, INLINE_ICON_COUNT - 1), selected] : inline;
    }, [formik.values.icon]);

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <DotLottie />
            </div>
        );
    }

    const widgetColor = formik.values.primaryColor || DEFAULT_COLOR;

    return (
        <div className="container-fluid p-0 hc-ai-page hc-ai-form">
            <form onSubmit={formik.handleSubmit} className="hc-ai-form-shell bg-white">
                <div className="hc-ai-form-head d-flex flex-wrap align-items-center justify-content-between">
                    <div className="d-flex align-items-center gap-3" style={{ minWidth: 0 }}>
                        <button
                            type="button"
                            className="btn btn-light border rounded-circle shadow-sm flex-shrink-0"
                            onClick={() => navigate(-1)}
                            style={{ width: 40, height: 40 }}
                            aria-label="Go back"
                        >
                            &larr;
                        </button>
                        <div style={{ minWidth: 0 }}>
                            <h3 className="fw-bold mb-0 hc-text-primary">{isEdit ? "Edit Widget" : "Create Widget"}</h3>
                            <small className="text-muted">Customize the embeddable chat widget for this client</small>
                        </div>
                    </div>
                </div>

                <div className="hc-ai-form-body d-grid align-items-stretch is-aside-end">
                    <div className="hc-ai-form-main">
                        <AiFormSection icon={MdEditNote} title="Widget Details" subtitle="Name and describe the assistant your client's visitors will see">
                            <div className="row g-3">
                                {!kbLoading && !isEdit && (isSuperAdmin ? clientOptions.length === 0 : !selectedClientReady) ? (
                                    <div className="col-12">
                                        <div className="hc-ai-kb-notice d-flex align-items-start gap-2">
                                            <MdInfoOutline size={18} className="flex-shrink-0" />
                                            <span>
                                                {isSuperAdmin
                                                    ? "No client has a completed knowledge base yet. Upload documents in Knowledge Base and wait for indexing to finish before building a widget."
                                                    : "Your knowledge base is still being prepared. You can build a widget once at least one document has finished indexing."}
                                            </span>
                                        </div>
                                    </div>
                                ) : null}
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">Title {renderAsterisk()}</label>
                                    <input
                                        className={`form-control ${formik.touched.title && formik.errors.title ? "is-invalid" : ""}`}
                                        name="title"
                                        placeholder="e.g. Support Assistant"
                                        {...formik.getFieldProps("title")}
                                    />
                                    <div className="invalid-feedback">{formik.errors.title}</div>
                                </div>

                                {isSuperAdmin ? (
                                    <div className="col-md-6">
                                        <SearchableDropdown
                                            label="Client"
                                            placeholder="Select client"
                                            options={clientOptions}
                                            value={formik.values.clientId ?? ""}
                                            onChange={(value) => formik.setFieldValue("clientId", value || null)}
                                            disabled={clientsLoading}
                                            loading={clientsLoading}
                                            required
                                            emptyMessage="No clients with a completed knowledge base"
                                            error={formik.touched.clientId && formik.errors.clientId ? formik.errors.clientId : ''}
                                        />
                                        <div className="form-text">Only clients whose knowledge base has finished indexing are listed.</div>
                                    </div>
                                ) : (
                                    <div className="col-md-6">
                                        <SearchableDropdown
                                            label="Client"
                                            placeholder={kbLoading ? "Loading..." : "Your organization"}
                                            options={accountId ? [{ value: accountId, label: ownClientName || "Your organization" }] : []}
                                            value={accountId ?? ""}
                                            onChange={() => {}}
                                            disabled
                                            loading={kbLoading}
                                            required
                                        />
                                        <div className="form-text">Widgets are built for your own organization.</div>
                                    </div>
                                )}

                                <div className="col-12">
                                    <label className="form-label fw-semibold">Description</label>
                                    <textarea
                                        className="form-control"
                                        name="description"
                                        rows={3}
                                        placeholder="Shown under the widget title in the chat panel"
                                        {...formik.getFieldProps("description")}
                                    />
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-semibold">Fallback Message</label>
                                    <textarea
                                        className={`form-control ${formik.touched.fallbackMessage && formik.errors.fallbackMessage ? "is-invalid" : ""}`}
                                        name="fallbackMessage"
                                        rows={3}
                                        maxLength={1000}
                                        placeholder={"Shown to visitors when the assistant can't find an answer in the knowledge base or takes too long to respond. Leave empty to use the default message.\ne.g. Sorry, I couldn't find that. Please call us at (555) 010-0000 or email support@example.com."}
                                        {...formik.getFieldProps("fallbackMessage")}
                                    />
                                    <div className="invalid-feedback">{formik.errors.fallbackMessage}</div>
                                </div>
                            </div>
                        </AiFormSection>

                        <AiFormSection icon={MdPalette} title="Branding & Appearance" subtitle="Match the widget to the client's brand and screen layout">
                            <div className="row g-3">
                                <div className="col-md-5">
                                    <label className="form-label fw-semibold">Primary Color {renderAsterisk()}</label>
                                    <div className="d-flex align-items-center gap-2">
                                        <input
                                            type="color"
                                            className="form-control form-control-color flex-shrink-0"
                                            style={{ width: 52, height: 46 }}
                                            value={formik.values.primaryColor}
                                            onChange={(event) => formik.setFieldValue("primaryColor", event.target.value)}
                                            aria-label="Pick primary color"
                                        />
                                        <input
                                            className={`form-control ${formik.touched.primaryColor && formik.errors.primaryColor ? "is-invalid" : ""}`}
                                            value={formik.values.primaryColor}
                                            onChange={(event) => formik.setFieldValue("primaryColor", event.target.value)}
                                            placeholder="#1c4fa6"
                                        />
                                    </div>
                                    <div className="invalid-feedback d-block">{formik.touched.primaryColor ? formik.errors.primaryColor : ''}</div>
                                </div>

                                <div className="col-md-7">
                                    <div className="d-flex align-items-center justify-content-between mb-1">
                                        <label className="form-label fw-semibold mb-0">Launcher Icon</label>
                                        <span className="text-muted small">{getAssistIconLabel(formik.values.icon)}</span>
                                    </div>
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        {displayIcons.map((option) => {
                                            const active = formik.values.icon === option.value;
                                            const OptionIcon = option.Icon;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    title={option.label}
                                                    aria-label={option.label}
                                                    aria-pressed={active}
                                                    onClick={() => selectPresetIcon(option.value)}
                                                    className={`hc-ai-icon-choice d-inline-flex align-items-center justify-content-center p-0 ${active ? "is-active" : ""}`}
                                                    style={active ? { backgroundColor: widgetColor } : undefined}
                                                >
                                                    <OptionIcon size={19} />
                                                </button>
                                            );
                                        })}
                                        <button
                                            type="button"
                                            className="hc-ai-icon-more-btn d-inline-flex align-items-center gap-1"
                                            onClick={() => setIsIconModalOpen(true)}
                                            title="Browse all icons"
                                            aria-label="Browse all icons"
                                        >
                                            <MdApps size={18} />
                                            <span>More</span>
                                        </button>
                                    </div>
                                    <div className="form-text">Shown on the floating chat button.</div>
                                </div>

                                <div className="col-12">
                                    <label className="form-label fw-semibold mb-1">Logo {renderAsterisk()}</label>
                                    <div className="d-flex flex-wrap align-items-center gap-2">
                                        {logoUrl ? (
                                            <span
                                                className="hc-ai-icon-choice is-active d-inline-flex align-items-center justify-content-center p-0"
                                                style={{ backgroundColor: widgetColor }}
                                                title="Logo"
                                            >
                                                <ChatLogo size={22} />
                                            </span>
                                        ) : null}
                                        <button
                                            type="button"
                                            className={`hc-ai-icon-more-btn d-inline-flex align-items-center gap-1 ${logoError ? "border-danger text-danger" : ""}`}
                                            onClick={() => logoInputRef.current?.click()}
                                        >
                                            <MdFileUpload size={18} />
                                            <span>{logoUrl ? "Replace Logo" : "Upload Logo"}</span>
                                        </button>
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept={LOGO_TYPES.join(",")}
                                            className="d-none"
                                            onChange={handleLogoSelect}
                                        />
                                    </div>
                                    {logoError ? <div className="invalid-feedback d-block">{logoError}</div> : null}
                                    <div className="form-text">Shown inside the chat window. PNG, JPEG or WebP, up to 1 MB.</div>
                                </div>

                                <div className="col-12 pt-1">
                                    <label className="form-label fw-semibold">Chat Panel Position</label>
                                    <div className="hc-ai-position-grid" style={{ "--widget-color": widgetColor }}>
                                        {ASSIST_POSITION_OPTIONS.map((option) => {
                                            const active = (formik.values.position || "bottom-right") === option.value;
                                            return (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    onClick={() => formik.setFieldValue("position", option.value)}
                                                    className={`hc-ai-position-option d-flex align-items-center gap-2 ${active ? "is-active" : ""}`}
                                                    aria-pressed={active}
                                                >
                                                    <span className="hc-ai-position-icon-box position-relative">
                                                        <span className={`hc-ai-position-dot hc-ai-pos-dot-${option.value}`} />
                                                    </span>
                                                    <span className="fw-semibold text-truncate">{option.label}</span>
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </AiFormSection>
                    </div>

                    <aside className="hc-ai-form-aside">
                        <div className="hc-ai-sticky-lg">
                            <div className="hc-ai-form-section-head d-flex align-items-center mb-3">
                                <span className="hc-ai-card-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                    <MdVisibility size={19} />
                                </span>
                                <div style={{ minWidth: 0 }}>
                                    <h6 className="hc-ai-card-title m-0">Live Preview</h6>
                                    <p className="hc-ai-card-sub">Updates as you type</p>
                                </div>
                            </div>
                            <div className="small mb-3" style={{ color: "var(--ai-muted)" }}>
                                This is how the floating widget will appear on the client's website. Downloadable embed scripts are coming in a later phase.
                            </div>
                            <div className="hc-ai-browser rounded-4 bg-white overflow-hidden" style={{ "--widget-color": widgetColor }}>
                                <div className="hc-ai-browser-bar align-items-center" aria-hidden="true">
                                    <i />
                                    <i />
                                    <i />
                                    <span className="hc-ai-browser-url ms-2 rounded-2 bg-white" />
                                </div>
                                <div className={`hc-ai-browser-stage hc-ai-stage-${formik.values.position || 'bottom-right'} position-relative p-3`}>
                                    <div aria-hidden="true">
                                        <div className="hc-ai-skel-line rounded-pill" style={{ width: "50%", height: 12 }} />
                                        <div className="hc-ai-skel-line rounded-pill" style={{ width: "80%" }} />
                                        <div className="hc-ai-skel-line rounded-pill" style={{ width: "66%" }} />
                                        <div className="hc-ai-skel-line rounded-pill" style={{ width: "45%" }} />
                                    </div>
                                    <div className={`hc-ai-widget pos-${formik.values.position || 'bottom-right'} position-absolute rounded-3 bg-white overflow-hidden`}>
                                        <div className="hc-ai-widget-head d-flex align-items-center text-white">
                                            <span className="hc-ai-widget-head-icon d-inline-flex align-items-center justify-content-center flex-shrink-0">
                                                <ChatLogo size={13} />
                                            </span>
                                            <span className="fw-semibold text-truncate" style={{ fontSize: 11 }}>{formik.values.title || 'Widget Title'}</span>
                                        </div>
                                        <div className="hc-ai-widget-body d-flex flex-column gap-1">
                                            <div className="hc-ai-widget-bubble">{formik.values.description || 'Hi.'}</div>
                                            <div className="hc-ai-widget-bubble hc-ai-widget-bubble-user align-self-end">Hi there!</div>
                                        </div>
                                        <div className="hc-ai-widget-input rounded-pill" />
                                    </div>
                                    <span className={`hc-ai-launcher pos-${formik.values.position || 'bottom-right'} position-absolute rounded-circle d-inline-flex align-items-center justify-content-center text-white`}>
                                        <LauncherIcon size={18} />
                                    </span>
                                </div>
                            </div>

                            {selectedClientReady ? (
                                <div className={`hc-ai-trial-card d-flex flex-wrap align-items-center justify-content-between ${trialState === "on" ? "is-live" : ""}`}>
                                    <div style={{ minWidth: 0, flex: "1 1 180px" }}>
                                        <div className="hc-ai-trial-title d-flex align-items-center gap-2 fw-bold">
                                            {trialState === "on" ? <span className="hc-ai-trial-live-dot" aria-hidden="true" /> : null}
                                            {trialState === "on" ? "Trial is live" : "Try it live"}
                                        </div>
                                        <div className="hc-ai-trial-sub">
                                            {trialState === "on"
                                                ? "Click the floating icon in the corner of your screen."
                                                : `Chat with ${selectedClientName ? `${selectedClientName}'s` : "your"} knowledge base through this widget.`}
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className={`hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap ${trialState === "on" ? "hc-ai-btn-outline" : "hc-ai-btn-primary"}`}
                                        onClick={toggleTrial}
                                        disabled={trialState === "exiting"}
                                    >
                                        {trialState === "on" ? <LuSquare size={14} /> : <LuPlay size={15} />}
                                        {trialState === "on" ? "End Trial" : "Start Trial"}
                                    </button>
                                </div>
                            ) : selectedClientId && !kbLoading ? (
                                <div className="hc-ai-trial-hint">The live trial unlocks once this client's knowledge base has finished indexing.</div>
                            ) : !selectedClientId ? (
                                <div className="hc-ai-trial-hint">Select a client to try this widget live against their knowledge base.</div>
                            ) : null}
                        </div>
                    </aside>
                </div>

                <div className="hc-ai-form-foot d-flex flex-wrap justify-content-end">
                    <button type="button" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-outline" onClick={() => navigate(-1)} disabled={formik.isSubmitting}>
                        Cancel
                    </button>
                    <button type="submit" className="hc-ai-btn overflow-hidden d-inline-flex align-items-center justify-content-center gap-2 fw-bold text-nowrap hc-ai-btn-primary px-4" disabled={formik.isSubmitting}>
                        {formik.isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <MdSave size={18} />
                                Save
                            </>
                        )}
                    </button>
                </div>
            </form>

            {trialState !== "off" ? (
                <AssistTrialWidget
                    key={selectedClientId}
                    token={token}
                    clientId={isSuperAdmin ? selectedClientId : undefined}
                    title={formik.values.title || "AI Assistant"}
                    description={formik.values.description}
                    primaryColor={widgetColor}
                    icon={LauncherIcon}
                    logo={ChatLogo}
                    position={formik.values.position || "bottom-right"}
                    fallbackMessage={formik.values.fallbackMessage}
                    exiting={trialState === "exiting"}
                    onExited={handleTrialExited}
                />
            ) : null}

            {savedWidget ? (
                <AssistEmbedModal
                    agentId={savedWidget.id}
                    title={savedWidget.title}
                    icon={ChatLogo}
                    onClose={() => navigate(APPRoutes.AEGIS_AI_ASSIST)}
                />
            ) : null}

            <AssistIconPickerModal
                isOpen={isIconModalOpen}
                onClose={() => setIsIconModalOpen(false)}
                selectedIcon={formik.values.icon}
                onSelectIcon={selectPresetIcon}
                primaryColor={widgetColor}
            />
        </div>
    );
}
